/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { Icon, Pressable, View } from '@components/atoms'
import { Tab as MoleculesTab } from '@components/molecules'
import { Draggable } from 'react-beautiful-dnd'

import {
	useAppDispatch,
	routeActions,
	useAppSelector,
	ConfigStateType,
} from '@redux/index'
import { grey, blue } from '@mui/material/colors'
import { I18nType } from '@schemas'

interface PropsType {
	key: string
	id: string
	path: string
	index: number
	value: string
	label: string
	i18n: I18nType[]
}

export const DraggableTab = (props: PropsType) => {
	// redux dispatch
	const reduxDispatch = useAppDispatch()
	const userLanguage: string = useAppSelector(state => state.user.language)

	// redux state
	const mode: ConfigStateType['mode'] = useAppSelector(state => state.config.mode)

	// state
	const [isRemoveButtonHovering, setIsRemoveButtonHovering] =
		React.useState<boolean>(false)

	// variables
	const isHiddenTab: boolean = props.index === 0
	const i18n: I18nType[] = props.i18n ?? []
	const thisI18n =
		i18n.length > 0
			? i18n.filter(anI18n => anI18n.isoLanguageCode === userLanguage.toLowerCase())
			: []
	const title: string = thisI18n.length > 0 ? thisI18n[0].title : ''

	const onRemoveButtonMouseOver = React.useCallback(() => {
		setIsRemoveButtonHovering(true)
	}, [])

	const onRemoveButtonMouseLeave = React.useCallback(() => {
		setIsRemoveButtonHovering(false)
	}, [])

	const a11yProps = React.useCallback((index: number) => {
		return {
			id: `full-width-tab-${index}`,
			'aria-controls': `full-width-tabpanel-${index}`,
		}
	}, [])

	const ChildTab = (
		<MoleculesTab
			key={props.id}
			label={props.label}
			value={`Tab ${props.index + 1}`}
			{...a11yProps(props.index)}
			className={`muiTabChildren ${
				isRemoveButtonHovering ? 'hovering-muiTabChildren' : ''
			}`}
			css={{
				'&:after': {
					borderRight: `1px solid ${mode === 'admin' ? blue[600] : grey[100]}`,
				},
			}}
		/>
	)

	const onRemoveClick = React.useCallback(() => {
		reduxDispatch(routeActions.REMOVE_TAB_IDX(props.index))
	}, [props.index])

	return (
		<Draggable
			key={`${props.index}`}
			draggableId={`${props.index}`}
			index={props.index}
			disableInteractiveElementBlocking
		>
			{draggableProvided => {
				return (
					<View
						css={[style, isHiddenTab ? { width: '0.1px', padding: 0 } : {}]}
						ref={draggableProvided.innerRef}
						{...draggableProvided.draggableProps}
					>
						{React.cloneElement(ChildTab, {
							...props,
							...draggableProvided.dragHandleProps,
							label: title !== '' ? title : props.label,
							style: {
								cursor: 'pointer',
								background: mode === 'admin' ? blue[700] : '#fff',
								color: mode === 'admin' ? '#fff' : grey[700],
							},
						})}

						{!isHiddenTab && (
							<Pressable
								className="removeButton"
								onClick={onRemoveClick}
								onMouseOver={onRemoveButtonMouseOver}
								onMouseLeave={onRemoveButtonMouseLeave}
							>
								<Icon
									name="close"
									style={{
										position: 'absolute',
										fontSize: '14px',
										color: mode === 'admin' ? blue[50] : grey[800],
									}}
									strokeWidth="1px"
									strokeColor={grey[800]}
								/>
							</Pressable>
						)}
					</View>
				)
			}}
		</Draggable>
	)
}

const style = css({
	position: 'relative',
	padding: '0 0.1rem 0.1rem',

	'.muiTabChildren': {
		overflow: 'initial',
		minHeight: '2.4rem',
		height: '2.3rem',
		width: 'fit-content',
		minWidth: '6.5rem',
		margin: 0,
		marginBottom: 0,
		padding: '0.2rem 0.5rem',
		paddingRight: '1.5rem',
		alignItems: 'flex-start',
		color: grey[500],
		fontSize: '12px',
		textTransform: 'none',
		opacity: 1,

		'&:after': {
			content: '""',
			position: 'absolute',
			width: '1px',
			height: '1rem',
			right: '-0.1rem',
			top: 0,
			bottom: 0,
			margin: 'auto',
		},

		'&.Mui-selected': {
			height: '2rem',
			background: '#fff',
			color: grey[800],
		},

		'&.Mui-selected:hover': {
			background: '#fff',
		},

		'&:hover': {
			color: grey[900],
		},
	},

	'.hovering-muiTabChildren': {
		color: grey[900],
	},

	'.removeButton': {
		position: 'absolute',
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		width: '1.5rem',
		height: '1.5rem',
		top: 0,
		bottom: '0rem',
		right: '0.3rem',
		margin: 'auto',
		borderRadius: '5px',
		opacity: 0.1,

		'&.removeButton:hover': {
			opacity: 0.7,
		},
	},
})
